<script>
import axios from 'axios';
import { formatDateTime } from '../../Modules/Dates.js';
import { shopHasFeature } from '../../Modules/ShopManagement.js';
import { HISTORY_PULL } from '../../Constants/ShopManagementFeatures.js';

export default {
  name: 'CallerID',
  data() {
    return {
      shop_id: this.$page.props.master_config.shop_id,
      master_config: this.$page.props.master_config,
      ws: null,
      pingInterval: null,
      notifications: [],
      // swap to sandbox URL when testing
      rcServer: 'https://platform.ringcentral.com',
      apiToken: null,
      opts: {
        eventFilters: [`/restapi/v1.0/account/~/extension/~/presence?detailedTelephonyState=true`],
      },
      customersCache: {},
      wsToken: null,
      wsUri: null,
      wsTokenExpiresAt: 0,
    };
  },
  methods: {
    formatDateTime,
    shopHasFeature,
    HISTORY_PULL() {
      return HISTORY_PULL;
    },
    async subscribeWebSocket() {
      try {
        // 1. fetch your JWT/OAuth token from your backend
        const { data: tokenData } = await axios.get(
          route('inbound-phone-call.ringcentral.token', { shop_id: this.shop_id }),
        );
        const apiToken = tokenData.data.access_token;
        this.apiToken = apiToken;

        // 2. get a one‑time WS token + URI (with retry on 429)
        const { ws_access_token, uri } = await this.getWsCredentials();

        // 3. open native WebSocket
        this.ws = new WebSocket(`${uri}?access_token=${ws_access_token}`);

        this.ws.onopen = () => {
          console.log('Ring Central WebSocket connected');
          this._subscribe();
          this.fetchPresence();
        };

        this.ws.onmessage = async ({ data }) => {
          const msg = JSON.parse(data);
          if (msg[0]?.type === 'ServerNotification') {
            const payload = msg[1];
            //console.log('🔔 Notification', payload);
            const status = payload.body?.telephonyStatus;
            if (status === 'Ringing') {
              // Incoming call starting
              const inboundCall = payload.body.activeCalls?.find((c) => c.direction === 'Inbound');
              const rawNumber = inboundCall?.from;
              const strippedNumber = rawNumber.slice(-10);
              const customer = await this.getCustomerDetails(strippedNumber, true);
              const displayName = customer?.details?.firstname
                ? `${customer.details.firstname} ${customer.details.lastname}`
                : `Unknown Customer`;
              this.notifications.unshift({
                id: `${strippedNumber}-${Date.now()}`,
                event: 'Incoming Call',
                callerNumber: strippedNumber,
                callerName: displayName,
                summary: customer.summary,
                timestamp: new Date().toLocaleTimeString(),
              });
            } else if (status === 'CallConnected') {
              const inboundCall = payload.body?.activeCalls?.find((c) => c.direction === 'Inbound');
              const rawNumber = inboundCall?.from;
              const strippedNumber = rawNumber?.slice(-10);
              // Remove existing incoming call notifications for this number
              this.notifications = this.notifications.filter(
                (n) => !(n.event === 'Incoming Call' && n.callerNumber === strippedNumber),
              );
              const customer = await this.getCustomerDetails(strippedNumber, true);
              const displayName = customer?.details?.firstname
                ? `${customer.details.firstname} ${customer.details.lastname}`
                : 'Unknown Customer';
              const notif = {
                id: `${strippedNumber}-${Date.now()}`,
                event: 'Call Answered',
                callerNumber: strippedNumber,
                callerName: displayName,
                summary: customer?.summary,
                timestamp: new Date().toLocaleTimeString(),
                remaining: 20,
              };
              this.notifications.unshift(notif);
              this.$nextTick(() => {
                console.log('Starting countdown for', notif);
                this.startCountdown(notif);
              });
            } else if (status === 'NoCall') {
              // Call ended: remove any "Incoming Call" entries
              this.notifications = this.notifications.filter((n) => n.event !== 'Incoming Call');
            } else {
              // Other status updates
              this.notifications.unshift({
                event: payload.event || 'Notification',
                details: payload.body,
                timestamp: new Date().toLocaleTimeString(),
              });
            }
          }
        };

        this.ws.onerror = (e) => console.error('WebSocket error', e);
        this.ws.onclose = (e) => {
          console.warn('WebSocket closed', e);
          clearInterval(this.pingInterval);
        };

        // 4. ping every 55s to keep alive
        this.pingInterval = setInterval(() => {
          if (this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'Ping' }));
          }
        }, 55_000);
      } catch (e) {
        console.error('subscribeWebSocket failed', e);
      }
    },

    _subscribe() {
      const req = {
        eventFilters: this.opts.eventFilters,
        deliveryMode: { transportType: 'WebSocket' },
      };
      const msg = [
        {
          type: 'ClientRequest',
          messageId: Math.random().toString(36).substr(2),
          method: 'POST',
          path: '/restapi/v1.0/subscription',
        },
        req,
      ];
      this.ws.send(JSON.stringify(msg));
    },

    async fetchPresence() {
      try {
        const { data: pres } = await axios.get(
          `${this.rcServer}/restapi/v1.0/account/~/extension/~/presence?detailedTelephonyState=true`,
          { headers: { Authorization: `Bearer ${this.apiToken}` } },
        );
        if (pres.telephonyStatus === 'Ringing') {
          const inboundCall = pres.activeCalls?.find((c) => c.direction === 'Inbound');
          if (inboundCall) {
            const rawNumber = inboundCall?.from;
            const strippedNumber = rawNumber.slice(-10);
            const customer = await this.getCustomerDetails(strippedNumber, true);
            const displayName =
              customer && customer.details.firstname
                ? `${customer.details.firstname} ${customer.details.lastname}`
                : `Unknown Customer`;
            this.notifications.unshift({
              event: 'Incoming Call',
              callerNumber: strippedNumber,
              callerName: displayName,
              summary: customer.summary,
              timestamp: new Date().toLocaleTimeString(),
            });
          }
        }
      } catch (e) {
        console.error('fetchPresence error', e);
      }
    },

    async getCustomerDetails(phone_number, refresh = false) {
      // Return cached entry if available
      if (!refresh && this.customersCache[phone_number]) {
        return this.customersCache[phone_number];
      }
      try {
        const { data } = await axios.get(
          route('inbound-phone-call.details', {
            shop_id: this.shop_id,
            phone_number: phone_number,
          }),
        );
        // Cache the result and return it
        this.customersCache[phone_number] = {
          details: data.data.customer,
          summary: data.data.summary,
        };
        return this.customersCache[phone_number];
      } catch (e) {
        console.error('getCustomerDetails error', e);
        const unknown = { unknown: true };
        this.customersCache[phone_number] = unknown;
        return unknown;
      }
    },

    async getWsCredentials() {
      // try reading from localStorage
      const lsToken = localStorage.getItem('rc_ws_token');
      const lsUri = localStorage.getItem('rc_ws_uri');
      const lsExpires = parseInt(localStorage.getItem('rc_ws_expires'), 10) || 0;

      if (lsToken && lsUri && Date.now() < lsExpires) {
        this.wsToken = lsToken;
        this.wsUri = lsUri;
        this.wsTokenExpiresAt = lsExpires;
        return { ws_access_token: lsToken, uri: lsUri };
      }

      // otherwise fetch fresh
      const { ws_access_token, uri, expires_in } = await this.requestWsToken();
      const expiresAt = Date.now() + expires_in * 1000 - 5_000; // 5s buffer

      // write to localStorage
      localStorage.setItem('rc_ws_token', ws_access_token);
      localStorage.setItem('rc_ws_uri', uri);
      localStorage.setItem('rc_ws_expires', expiresAt.toString());

      // and in-memory
      this.wsToken = ws_access_token;
      this.wsUri = uri;
      this.wsTokenExpiresAt = expiresAt;

      return { ws_access_token, uri };
    },

    async requestWsToken(attempt = 1) {
      try {
        const { data } = await axios.post(`${this.rcServer}/restapi/oauth/wstoken`, null, {
          headers: { Authorization: `Bearer ${this.apiToken}` },
        });
        return data;
      } catch (e) {
        const status = e.response?.status;
        if (status === 429 && attempt < 5) {
          const retryAfter = parseInt(e.response.headers['retry-after'], 10) || attempt * 2;
          console.warn(`WSToken 429; retrying in ${retryAfter}s (attempt ${attempt})`);
          await new Promise((res) => setTimeout(res, retryAfter * 1000));
          return this.fetchWsToken(attempt + 1);
        }
        console.error('fetchWsToken error', e.response || e);
        throw e;
      }
    },
    startCountdown(notification) {
      console.log('startCountdown invoked for', notification);
      const interval = setInterval(() => {
        console.log('Countdown tick:', notification.remaining);
        // Decrement remaining directly for reactivity
        notification.remaining--;
        this.$forceUpdate();
        if (notification.remaining <= 0) {
          clearInterval(interval);
          const idx = this.notifications.findIndex((n) => n.id === notification.id);
          if (idx !== -1) this.notifications.splice(idx, 1);
        }
      }, 1000);
    },
    openNotificationDvi(notification) {
      if (!notification?.summary?.dvi_url) return;
      window.open(notification.summary.dvi_url, '_blank', 'noopener');
    },
    hasActiveTicketSummary(summary) {
      return !!(summary?.has_active_ticket || summary?.current_status_name);
    },
  },
  mounted() {
    this.subscribeWebSocket();
  },
  beforeDestroy() {
    clearInterval(this.pingInterval);
    if (this.ws) this.ws.close();
  },
};
</script>

<template>
  <div class="caller-id" v-if="notifications.length">
    <ul>
      <li
        class="caller-id-event"
        v-for="notif in notifications"
        :key="notif.id"
        :class="{ 'caller-id-event--link': notif.summary?.dvi_url }"
        :role="notif.summary?.dvi_url ? 'link' : null"
        :tabindex="notif.summary?.dvi_url ? 0 : null"
        @click="openNotificationDvi(notif)"
        @keydown.enter="openNotificationDvi(notif)"
      >
        <i
          class="fa fa-times"
          aria-hidden="true"
          @click.stop="notifications.splice(notifications.indexOf(notif), 1)"
        ></i>
        <strong>
          <i
            class="fas fa-phone"
            aria-hidden="true"
            :class="{ buzz: notif.event === 'Incoming Call' }"
          ></i>
          {{ notif.event }}
        </strong>
        <span v-if="notif.callerName"> : {{ notif.callerName }} ({{ notif.callerNumber }}) </span>
        <span v-else-if="notif.callerNumber"> : {{ notif.callerNumber }} </span>
        <small v-if="notif.timestamp"> ({{ notif.timestamp }})</small>
        <div v-if="notif.remaining !== undefined" style="padding-left: 15px; color: red">
          Closing in {{ notif.remaining }}s
        </div>
        <div v-if="notif.summary" style="padding-left: 15px">
          <strong>Summary:</strong>
          <ul class="summary">
            <li v-if="hasActiveTicketSummary(notif.summary) && notif.summary.current_status_name">
              <strong>Current Status:</strong>
              {{ notif.summary.current_status_name }}
            </li>
            <li
              v-if="hasActiveTicketSummary(notif.summary) && notif.summary.current_status_vehicle"
            >
              <strong>Current Visit Vehicle:</strong>
              {{ notif.summary.current_status_vehicle }}
            </li>
            <li v-if="notif.summary.dvi_url">
              <strong>DVI:</strong>&nbsp;
              <a :href="notif.summary.dvi_url" target="_blank" rel="noopener" @click.stop>
                <template v-if="notif.summary.dvi_completed_at">
                  Completed on {{ formatDateTime(notif.summary.dvi_completed_at) }}
                </template>
                <template v-else>
                  {{ notif.summary.dvi_label || 'View DVI' }}
                </template>
                <i class="fa fa-external-link caller-id-external-link" aria-hidden="true"></i>
              </a>
            </li>
            <li v-if="notif.summary.wo_url">
              <strong>WO:</strong>&nbsp;
              <a :href="notif.summary.wo_url" target="_blank" rel="noopener" @click.stop>
                <template v-if="notif.summary.wo_completed_at">
                  Completed on {{ formatDateTime(notif.summary.wo_completed_at) }}
                </template>
                <template v-else-if="notif.summary.wo_generated_at">
                  {{ notif.summary.wo_status }} on
                  {{ formatDateTime(notif.summary.wo_generated_at) }}
                </template>
                <template v-else>
                  {{ notif.summary.wo_status || 'View WO' }}
                </template>
                <i class="fa fa-external-link caller-id-external-link" aria-hidden="true"></i>
              </a>
            </li>
            <li v-if="hasActiveTicketSummary(notif.summary) && notif.summary.current_status_date">
              <strong>Last Update:</strong>
              {{ formatDateTime(notif.summary.current_status_date) }}
            </li>
            <template v-for="(value, key) in notif.summary" :key="key">
              <li
                v-if="!hasActiveTicketSummary(notif.summary) && key === 'last_visit_date' && value"
              >
                <strong>Last Visit:</strong>
                {{ formatDateTime(value) }}
              </li>
              <li
                v-else-if="
                  !hasActiveTicketSummary(notif.summary) && key === 'last_visit_vehicle' && value
                "
              >
                <strong>Last Visit Vehicle:</strong>
                {{ value }}
              </li>
              <li v-else-if="key === 'last_visits_count' && value > 0">
                <strong>Visit Count:</strong>
                {{ value }}
              </li>
              <li
                v-else-if="
                  shopHasFeature(master_config, HISTORY_PULL()) &&
                  key === 'total_spend' &&
                  notif.summary.total_spend
                "
              >
                <strong>Total Spend:</strong>
                ${{ value }}
              </li>
              <li v-else-if="key === 'nps_score' && value > 0">
                <strong>NPS Score:</strong>
                {{ value }}
              </li>
            </template>
          </ul>
        </div>
      </li>
    </ul>
  </div>
</template>
